/**
 * The Booking's vehicle out/back times, one pair per day.
 *
 * Vehicle Windows are stored per (Vehicle, day), but the Operator plans them
 * per day: every vehicle on a job leaves and returns together, and a second
 * per-Vehicle time grid beside the Job Details day schedule was two tables
 * asking the same question. So the day schedule owns one pair of times per
 * day and this hook fans it out across every planned Vehicle — the stored
 * shape is unchanged, and Allocation Conflicts keep their per-Vehicle rows.
 *
 * There is no save button: a cell commits on blur like every other time on
 * that row.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  listVehicleWindows,
  putVehicleWindows,
  type VehicleWindow,
} from "./allocation-api";

export interface DayVehicleTimes {
  out: string;
  back: string;
}

const NO_TIMES: DayVehicleTimes = { out: "", back: "" };

/** The day's pair: the first planned Vehicle that carries a time on it. */
function timesForDate(
  windows: VehicleWindow[],
  date: string,
): DayVehicleTimes {
  const onDay = windows.filter((w) => w.window_date === date);
  const withTime = onDay.find((w) => w.start_time || w.end_time) ?? onDay[0];
  if (!withTime) return NO_TIMES;
  return { out: withTime.start_time ?? "", back: withTime.end_time ?? "" };
}

export function useVehicleDayTimes(bookingId: string) {
  const [windows, setWindows] = useState<VehicleWindow[]>([]);
  // Latest windows, readable inside a commit fired from a blur that happens
  // in the same tick as a state update.
  const latest = useRef<VehicleWindow[]>([]);
  const apply = useCallback((next: VehicleWindow[]) => {
    latest.current = next;
    setWindows(next);
  }, []);

  const read = useCallback(
    (signal?: AbortSignal) =>
      listVehicleWindows(bookingId, signal)
        .then(apply)
        // A missing time cell is a degraded row, never a broken section.
        .catch(() => {}),
    [bookingId, apply],
  );

  useEffect(() => {
    const ac = new AbortController();
    void read(ac.signal);
    return () => ac.abort();
  }, [read]);

  const commit = useCallback(
    (date: string, times: DayVehicleTimes) => {
      const start = times.out === "" ? null : times.out;
      const end = times.back === "" ? null : times.back;
      const changed = latest.current
        .filter((w) => w.window_date === date)
        .map((w) => ({ ...w, start_time: start, end_time: end }));
      if (changed.length === 0) return;
      // Optimistic, and the response is deliberately not synced back: it
      // re-states what was just sent, and landing mid-edit resets the pair.
      apply(
        latest.current.map(
          (w) => changed.find((c) => c.vehicle_id === w.vehicle_id && c.window_date === w.window_date) ?? w,
        ),
      );
      void putVehicleWindows(bookingId, changed).catch(() => {
        // The save failed; re-read rather than showing a time the server
        // does not have.
        void read();
      });
    },
    [bookingId, apply, read],
  );

  /**
   * Called after the planned Vehicles change. A newly selected Vehicle is
   * seeded server-side from the day schedule, which is not what the Operator
   * typed on these rows — so re-read, then give every Vehicle the day's pair.
   */
  const syncAfterVehicleChange = useCallback(async () => {
    await read();
    const dates = [...new Set(latest.current.map((w) => w.window_date))];
    const stale = dates.flatMap((date) => {
      const { out, back } = timesForDate(latest.current, date);
      const start = out === "" ? null : out;
      const end = back === "" ? null : back;
      return latest.current
        .filter(
          (w) =>
            w.window_date === date &&
            (w.start_time !== start || w.end_time !== end),
        )
        .map((w) => ({ ...w, start_time: start, end_time: end }));
    });
    if (stale.length === 0) return;
    apply(
      latest.current.map(
        (w) => stale.find((s) => s.vehicle_id === w.vehicle_id && s.window_date === w.window_date) ?? w,
      ),
    );
    await putVehicleWindows(bookingId, stale).catch(() => {});
  }, [bookingId, read, apply]);

  return {
    /** True once at least one Vehicle is planned — nothing to time before that. */
    planned: windows.length > 0,
    timesFor: useCallback(
      (date: string) => timesForDate(windows, date),
      [windows],
    ),
    commit,
    syncAfterVehicleChange,
  };
}

export type VehicleDayTimes = ReturnType<typeof useVehicleDayTimes>;
